import { FC, MouseEvent, ReactNode } from 'react';
import { ActionIcon, Box, Collapse, Group, Paper, Stack, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconCheck, IconChevronDown } from '@tabler/icons-react';
import styles from '@/PageComponents/Onboarding/components/OnboardingStepCard.module.css';

export interface OnboardingStepCardProps {
    index: number;
    stepId: string;
    title: string;
    description?: string;
    complete: boolean;
    /** Only steps with a body (children) open and close; the others just become active when clicked. */
    expanded?: boolean;
    highlighted: boolean;
    /** A click on the card: toggles a step with a body, selects one without. */
    onToggle: () => void;
    headerAction?: ReactNode;
    /** A small control kept in the header on every screen size, beside the chevron. */
    headerIcon?: ReactNode;
    /** Line the body up under the title (step 1's buttons) rather than across the whole card (step 2's tasks). */
    indentBody?: boolean;
    children?: ReactNode;
}

const OnboardingStepCard: FC<OnboardingStepCardProps> = ({
    index, stepId, title, description, complete, expanded = false, highlighted, onToggle, headerAction, headerIcon, indentBody = true, children,
}) => {
    const canToggle = !!children;
    // The header action sits beside the title from "sm" up, and below it on phones. Rendered once (not
    // twice, toggled by CSS) so its id stays unique for Clarity.
    const wide = useMediaQuery('(min-width: 48em)', true, { getInitialValueInEffect: false });
    // Anywhere on the card toggles (or selects) the step, padding included. Its own controls (the chevron,
    // header actions) and the open body, which holds the step's tasks, are left alone.
    const onCardClick = (e: MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        // React passes clicks in portals (dialogs opened from the header) up to the card too.
        if (!e.currentTarget.contains(target)) return;
        if (target.closest('button, a, input, label, [role="button"], [data-step-body]')) return;
        onToggle();
    };

    return (
    <Paper
        radius={4}
        className={`${styles.card} ${highlighted ? styles.highlighted : ''} ${styles.toggleable}`}
        onClick={onCardClick}
        id={`onboarding-step-${stepId}`}
        data-testid={`onboarding-step-${stepId}`}
        data-highlighted={highlighted}
        data-complete={complete}
    >
        <Group wrap="nowrap" align="flex-start" gap={20} data-testid={`onboarding-step-${stepId}-header`}>
            {complete ? (
                <div className={`${styles.marker} ${styles.markerComplete}`} role="img" aria-label="Step complete">
                    <IconCheck size={18} stroke={3} />
                </div>
            ) : (
                <div className={`${styles.marker} ${highlighted ? styles.markerActive : ''}`}>{index}</div>
            )}
            <Stack gap={8} flex={1} miw={0}>
                <Text className={styles.title}>{title}</Text>
                {description && <Text className={styles.description}>{description}</Text>}
            </Stack>
            {headerAction && wide && <Box>{headerAction}</Box>}
            {headerIcon}
            {canToggle && (
                <ActionIcon
                    variant="subtle"
                    radius="xl"
                    size={38}
                    id={`onboarding-step-${stepId}-toggle`}
                    className={styles.expand}
                    aria-label={`${expanded ? 'Collapse' : 'Expand'} ${title}`}
                    aria-expanded={expanded}
                    onClick={onToggle}
                >
                    <IconChevronDown size={22} stroke={2.5} className={`${styles.chevron} ${expanded ? styles.chevronOpen : ''}`} />
                </ActionIcon>
            )}
        </Group>
        {headerAction && !wide && <Box mt="sm" className={styles.mobileAction}>{headerAction}</Box>}
        {children && (
            <Collapse in={expanded} transitionDuration={200} transitionTimingFunction="ease">
                <Box pt={16} className={`${styles.body} ${indentBody ? styles.bodyIndented : ''}`} data-step-body>{children}</Box>
            </Collapse>
        )}
    </Paper>
    );
};

export default OnboardingStepCard;
